import { Color } from "three";
import type { HomeSectionId } from "../../content/siteContent";
import type { ViewportTier } from "../../lib/viewport";

export type CameraPose = {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
};

export const studioToProjectsDollyEnd = 0.62;

export const sectionSequence: HomeSectionId[] = ["studio", "projects", "team", "about", "contact"];

export const sectionBackgrounds: Record<HomeSectionId, Color> = {
  studio: new Color("#b8b6b5"),
  projects: new Color("#b8b6b5"),
  team: new Color("#b8b6b5"),
  about: new Color("#b8b6b5"),
  contact: new Color("#b8b6b5")
};

export function getSectionSceneWeight(
  id: HomeSectionId,
  current: HomeSectionId,
  target: HomeSectionId,
  progress: number,
  isTransitioning: boolean
) {
  if (!isTransitioning || current === target) {
    return current === id ? 1 : 0;
  }

  if (current === "studio" && target === "projects") {
    if (id === "studio") {
      if (progress < 0.58) {
        return 1;
      }

      if (progress > 0.8) {
        return 0;
      }

      return 1 - (progress - 0.58) / 0.22;
    }

    if (id === "projects") {
      if (progress < 0.76) {
        return 0;
      }

      return Math.min((progress - 0.76) / 0.24, 1);
    }
  }

  if (current === id) {
    if (progress < 0.44) {
      return 1;
    }

    if (progress > 0.62) {
      return 0;
    }

    return 1 - (progress - 0.44) / 0.18;
  }

  if (target === id) {
    if (progress < 0.68) {
      return 0;
    }

    return Math.min((progress - 0.68) / 0.2, 1);
  }

  return 0;
}

export function getSectionCameraPose(
  section: HomeSectionId,
  activeProjectIndex = 0,
  viewport: ViewportTier = "desktop"
): CameraPose {
  const mobile = viewport === "mobile";

  switch (section) {
    case "projects": {
      return {
        position: mobile ? [0, 0.52, 6.18] : [1.05, 0.42, 6.1],
        target: mobile ? [0, 0.18, -0.96] : [1.18, 0.12, -1.08],
        fov: mobile ? 34 : 33
      };
    }
    case "about":
      return {
        position: mobile ? [-0.12, 0.54, 6.7] : [-0.35, 0.5, 6.2],
        target: mobile ? [0.22, 0.22, -0.78] : [0, 0.3, -0.8],
        fov: mobile ? 34 : 36
      };
    case "team":
      return {
        position: mobile ? [0.06, 0.58, 6.68] : [0.02, 0.38, 6.35],
        target: mobile ? [0.06, 0.22, -1.14] : [0.02, 0.12, -1.12],
        fov: mobile ? 33 : 34
      };
    case "contact":
      return {
        position: mobile ? [0, 0.26, 5.7] : [0, 0.18, 5.2],
        target: mobile ? [0, -0.02, -0.42] : [0, 0, -0.4],
        fov: mobile ? 34 : 33
      };
    case "studio":
    default:
      return {
        position: mobile ? [0.12, 0.54, 5.82] : [0, 0.28, 7.1],
        target: mobile ? [0.44, -0.06, -0.22] : [0, 0.15, 0],
        fov: mobile ? 29.5 : 32
      };
  }
}

export function blendNumber(start: number, end: number, progress: number) {
  return start + (end - start) * progress;
}

export function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1);
}

export function easeInOutCubic(value: number) {
  const progress = clamp01(value);
  return progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

export function getStudioToProjectsDollyProgress(progress: number) {
  return easeInOutCubic(clamp01(progress / studioToProjectsDollyEnd));
}
